/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid util/csv.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import {
 columnsFor, formatterFor, headerFor, rowsFor, type ExportScope, type ExportTable
} from './export-table'

export interface CsvOptions {
  scope?: ExportScope
  delimiter?: string
  bom?: boolean
  sanitize?: boolean
}

export interface CsvDownloadOptions extends CsvOptions {
  filename?: string
}

export type CsvTable = ExportTable

const escapeField = (value: string, delimiter: string): string =>
  /["\r\n]/.test(value) || value.includes(delimiter) ? `"${value.replace(/"/g, '""')}"` : value

// CSV formula injection: a leading =, +, -, @, tab or CR makes spreadsheets treat
// the cell as a formula. Opt-in guard prefixes such fields with an apostrophe.
const sanitizeField = (value: string): string =>
  /^[=+\-@\t\r]/.test(value) ? `'${value}` : value

export const exportCsv = (table: CsvTable, options: CsvOptions = {}): string => {
  const delimiter = options.delimiter ?? ','
  const columns = columnsFor(table)

  const process = (field: string) => escapeField(options.sanitize ? sanitizeField(field) : field, delimiter)
  const line = (fields: string[]) => fields.map(process).join(delimiter)

  const header = line(columns.map(column => headerFor(column)))
  const body = rowsFor(table, options.scope).map(row =>
    line(columns.map(column => {
      const value = row.getValue(column.id)
      const formatter = formatterFor(column)
      // Coerce: a formatter may return a number/null in plain-JS usage.
      return formatter ? String(formatter(value, row.original) ?? '') : String(value ?? '')
    }))
  )

  const csv = [header, ...body].join('\r\n')
  return options.bom ? `\uFEFF${csv}` : csv
}

export const downloadCsv = (table: CsvTable, options: CsvDownloadOptions = {}): void => {
  const blob = new Blob([exportCsv(table, options)], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = options.filename ?? 'export.csv'
  anchor.style.display = 'none'
  // Attach before clicking (older Firefox) and defer revoke (Safari race).
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url))
}
